import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { LogoMark, Wordmark } from "./Logo";
import { STORE_URL } from "../../data/catalog";
import { scrollToId, ease } from "../../lib/scroll";

const links = [["Shop", "#shop"], ["Grades", "#grades"], ["Why us", "#why"], ["FAQ", "#faq"]];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (h) => { setOpen(false); scrollToId(h); };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease, delay: 0.1 }}
      className={`fixed inset-x-0 top-0 z-50 border-b border-white/[0.06] backdrop-blur-xl transition-colors duration-500 ${scrolled || open ? "bg-[#07080C]/90" : "bg-[#07080C]/60"}`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-4 sm:px-5 lg:px-10">
        <button onClick={() => { setOpen(false); window.__lenis?.scrollTo(0); }} className="flex items-center gap-2" data-testid="header-logo" aria-label="Back to top">
          <LogoMark className="h-10 w-10 sm:h-9 sm:w-9" />
          <Wordmark />
        </button>
        <nav className="hidden items-center gap-9 md:flex">
          {links.map(([l, h]) => (
            <button key={h} onClick={() => scrollToId(h)} className="nav-link font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-white" data-testid={`nav-${l.toLowerCase().replace(" ", "-")}`}>
              {l}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-1.5 rounded-full bg-volt px-4 py-2 text-sm font-bold text-black transition-transform duration-300 hover:-translate-y-0.5" data-testid="header-ebay-cta">
            The Vault
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </a>
          <button onClick={() => setOpen(!open)} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white md:hidden" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="mobile-menu-button">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease }}
            className="overflow-hidden border-t border-white/10 md:hidden"
            data-testid="mobile-menu-panel"
          >
            <div className="flex flex-col px-4 py-3">
              {links.map(([l, h], i) => (
                <button key={h} onClick={() => go(h)} className="flex items-center justify-between border-b border-white/[0.06] py-4 text-left font-display text-2xl font-extrabold uppercase text-white last:border-0" data-testid={`mobile-nav-${l.toLowerCase().replace(" ", "-")}`}>
                  {l}
                  <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
